import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { PaginatedList } from '../../../api/models/paginator/paginated-list';
import { SurveyRespondentService } from '../../../api/services/survey-respondent.service';
import { AuthService } from '../../../services/auth.service';
import { SurveyRespondentScheduledQuestionnaireModel } from '../../../api/models/survey-respondent-scheduled-questionnaire-model';
import { DialogService } from '../../../services/dialog.service';
import { NewScheduledQuestionnaireModalComponent } from '../../modals/new-scheduled-questionnaire-modal/new-scheduled-questionnaire-modal.component';
import { SurveyRespondentModel } from '../../../api/models/survey-respondent-model';

type RespondentScheduledQuestionnaireSortKey =
  'questionnaireName' | 'dispatchOn' | 'recipient';

@Component({
  selector: 'app-respondent-scheduled-questionnaires',
  templateUrl: './respondent-scheduled-questionnaires.component.html',
  standalone: false,
})
export class RespondentScheduledQuestionnairesComponent {
  surveyRespondentGuid?: string;
  surveyRespondentQuestionnairesPaginated?: PaginatedList<SurveyRespondentScheduledQuestionnaireModel>;
  sortKey?: RespondentScheduledQuestionnaireSortKey;
  sortDirection?: 'asc' | 'desc';
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true })
  surveyRespondentDetails!: SurveyRespondentModel;

  constructor(
    private route: ActivatedRoute,
    private surveyRespondentService: SurveyRespondentService,
    private authService: AuthService,
    private dialogService: DialogService,
  ) {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.surveyRespondentGuid = params['guid'];
        this.setQuestionnaires(this.surveyRespondentGuid!, 1);
      });
  }

  setQuestionnaires(surveyRespondentGuid: string, pageNumber: number) {
    this.surveyRespondentService
      .surveyRespondentScheduledQuestionnairesGet(
        this.authService.getAuthorisationHeaders(),
        {
          surveyRespondentGuid: surveyRespondentGuid,
          pageNumber: pageNumber,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paginatedList) => {
          this.surveyRespondentQuestionnairesPaginated =
            this.sortPaginatedQuestionnaires(paginatedList);
        },
      });
  }

  changePage(pageNumber: number) {
    this.setQuestionnaires(this.surveyRespondentGuid!, pageNumber);
  }

  getSortAriaLabel(
    label: string,
    sortKey: RespondentScheduledQuestionnaireSortKey,
  ): string {
    if (this.sortKey !== sortKey || !this.sortDirection) {
      return `${label}, not sorted. Activate to sort ascending.`;
    }

    if (this.sortDirection === 'asc') {
      return `${label}, sorted ascending. Activate to sort descending.`;
    }

    return `${label}, sorted descending. Activate to clear sorting.`;
  }

  sortResults(sortKey: RespondentScheduledQuestionnaireSortKey) {
    // Detail endpoints return paginated pages without sort params, so sort the loaded page only.
    if (this.sortKey === sortKey) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortDirection = undefined;
        this.sortKey = undefined;
      }
    } else {
      this.sortKey = sortKey;
      this.sortDirection = 'asc';
    }

    if (!this.sortDirection && this.surveyRespondentGuid) {
      this.setQuestionnaires(
        this.surveyRespondentGuid,
        this.surveyRespondentQuestionnairesPaginated?.pageNumber ?? 1,
      );
      return;
    }

    if (this.surveyRespondentQuestionnairesPaginated) {
      this.surveyRespondentQuestionnairesPaginated =
        this.sortPaginatedQuestionnaires(
          this.surveyRespondentQuestionnairesPaginated,
        );
    }
  }

  openCreateScheduledQuestionnaireModal() {
    const dialogRef = this.dialogService.openDialog(
      NewScheduledQuestionnaireModalComponent,
      {
        respondentDetails: this.surveyRespondentDetails,
        respondentGuid: this.surveyRespondentGuid,
      },
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (questionnaireScheduled: boolean) => {
          if (questionnaireScheduled) {
            this.setQuestionnaires(
              this.surveyRespondentGuid!,
              this.surveyRespondentQuestionnairesPaginated?.pageNumber ?? 1,
            );
          }
        },
      });
  }

  private sortPaginatedQuestionnaires(
    paginatedList: PaginatedList<SurveyRespondentScheduledQuestionnaireModel>,
  ): PaginatedList<SurveyRespondentScheduledQuestionnaireModel> {
    if (!this.sortKey || !this.sortDirection) {
      return paginatedList;
    }

    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return {
      ...paginatedList,
      records: [...paginatedList.records].sort((a, b) => {
        const aValue = this.getSortValue(a, this.sortKey!);
        const bValue = this.getSortValue(b, this.sortKey!);
        return aValue.localeCompare(bValue) * dir;
      }),
    };
  }

  private getSortValue(
    scheduledQuestionnaire: SurveyRespondentScheduledQuestionnaireModel,
    sortKey: RespondentScheduledQuestionnaireSortKey,
  ): string {
    switch (sortKey) {
      case 'questionnaireName':
        return scheduledQuestionnaire.questionnaireName?.toLowerCase() ?? '';
      case 'dispatchOn':
        return scheduledQuestionnaire.dispatchOn ?? '';
      case 'recipient':
        return scheduledQuestionnaire.recipient?.toLowerCase() ?? '';
      default:
        return '';
    }
  }
}
